import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Divider,
  Typography,
  Space,
  Alert,
} from 'antd';
import { SessionSecuritySetting } from './Settings/SessionSecuritySetting';
import {
  DollarOutlined,
  PercentageOutlined,
  ShopOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { shopSettingsApi } from '../services/api';
import { ShopSettings } from '../types';

const { Title, Text } = Typography;

interface ShopSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const ShopSettingsModal: React.FC<ShopSettingsModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadSettings();
    }
  }, [visible]);

  const loadSettings = async () => {
    try {
      setFetchLoading(true);
      const response = await shopSettingsApi.get();
      form.setFieldsValue(response.data);
    } catch (error) {
      console.error('Error loading shop settings:', error);
      message.error('Failed to load shop settings');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      await shopSettingsApi.update(values);
      message.success('Shop settings updated successfully!');
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        console.error('Error saving shop settings:', error);
        message.error('Failed to save shop settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <ShopOutlined />
          Shop Settings
        </Space>
      }
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
      centered
    >
      <Alert
        message="Default Labor Rate"
        description="This labor rate will be automatically applied when creating new line items in estimates. You can override it on a per-item basis."
        type="info"
        showIcon
        icon={<DollarOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          labor_rate: 175.00,
          tax_rate: 7,
        }}
      >
        <Divider orientation="left">
          <Text strong>Pricing</Text>
        </Divider>

        <Form.Item
          label={
            <Space>
              <DollarOutlined />
              <Text strong>Default Labor Rate ($/hour)</Text>
            </Space>
          }
          name="labor_rate"
          rules={[
            { required: true, message: 'Labor rate is required' },
            { type: 'number', min: 0, message: 'Labor rate must be positive' },
          ]}
          extra="Default hourly rate for labor charges"
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            step={5}
            precision={2}
            prefix="$"
            placeholder="175.00"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <PercentageOutlined />
              <Text strong>Default Tax Rate (%)</Text>
            </Space>
          }
          name="tax_rate"
          rules={[
            { required: true, message: 'Tax rate is required' },
            { type: 'number', min: 0, max: 100, message: 'Tax rate must be between 0 and 100' },
          ]}
          extra="Default sales tax rate applied to estimates"
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            max={100}
            step={0.25}
            precision={2}
            suffix="%"
            placeholder="7"
            size="large"
          />
        </Form.Item>

        <Divider orientation="left">
          <Text strong>Company Information (Optional)</Text>
        </Divider>

        <Form.Item
          label={
            <Space>
              <ShopOutlined />
              <Text>Company Name</Text>
            </Space>
          }
          name="company_name"
        >
          <Input placeholder="Your Shop Name" size="large" />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <HomeOutlined />
              <Text>Address</Text>
            </Space>
          }
          name="company_address"
        >
          <Input.TextArea
            placeholder="123 Main St, City, State ZIP"
            rows={2}
            size="large"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <PhoneOutlined />
              <Text>Phone</Text>
            </Space>
          }
          name="company_phone"
        >
          <Input placeholder="(555) 123-4567" size="large" />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <MailOutlined />
              <Text>Email</Text>
            </Space>
          }
          name="company_email"
          rules={[
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="contact@yourshop.com" size="large" />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <MailOutlined />
              <Text>Relay Email (Email to me)</Text>
            </Space>
          }
          name="relay_email"
          rules={[
            { type: 'email', message: 'Please enter a valid email' },
          ]}
          extra="Where the “Email to me” action sends estimates, work orders, and invoices so you can forward or text them to customers. Leave blank to use your account email."
        >
          <Input placeholder="you@example.com" size="large" allowClear />
        </Form.Item>
      </Form>
      <div style={{ marginTop: 16 }}>
        <SessionSecuritySetting />
      </div>
    </Modal>
  );
};

export default ShopSettingsModal;